import { apiUrls } from "../../actprep-ui/utils/urls";
import Box from "@mui/material/Box";
import PaginatedTable from "../../actprep-ui/components/PaginatedTable";
import layout from "../../actprep-ui/constants/layout";
import {
  sexyBooleanColumn,
  sexyBooleanColumnTrueOnly,
  sortingModes,
} from "../../actprep-ui/constants/dataGrid";
import { Chip, Drawer, Stack } from "@mui/material";
import {
  formatRecurrenceRule,
  fullName,
} from "../../actprep-ui/utils/formatting";
import dayjs from "dayjs";
import useDisclosure from "../../actprep-ui/hooks/ui/useDisclosure";
import { useFilters } from "../../actprep-ui/hooks/ui";
import { manageToDoItemsFilters } from "../../constants/filters";
import ToDoTemplateForm from "../../actprep-ui/components/ToDoTemplateForm";
import { dataGridNestedColumnGetter } from "../../actprep-ui/utils/tables";
import { useConfirm } from "material-ui-confirm";
import { showSuccessSnackbar } from "../../actprep-ui/utils/feedback";
import api from "../../actprep-ui/utils/api";
import { GridActionsCellItem } from "@mui/x-data-grid-pro";
import { Tooltip } from "@mui/material";
import { Delete } from "@mui/icons-material";
import { useState } from "react";
import { Typography, Paper } from "@mui/material";

export default function ToDoTemplatesTable() {
  const renderOptionCell = ({ value }) => (
    <Stack direction="row" spacing={1} pt={1}>
      {value.map(({ label }, i) => (
        <Chip key={i} label={label} size="small" />
      ))}
    </Stack>
  );
  const confirm = useConfirm();
  const filterDisc = useDisclosure();
  const { Filters, FiltersAppliedList, getFilterParams } = useFilters({
    name: "ToDoTemplates Table",
    saveState: true,
    filtersConfig: manageToDoItemsFilters,
    handleClose: filterDisc.onClose,
  });
  const [reload, setReload] = useState(false);
  const columns = [
    {
      field: "title",
      headerName: "Title",
      width: 200,
    },
    {
      field: "description",
      headerName: "Description",
      width: 200,
    },
    {
      field: "tags",
      headerName: "Tags",
      renderCell: renderOptionCell,
    },
    {
      field: "created_by",
      headerName: "Created By",
      valueFormatter: (val) => fullName(val?.user),
    },
    {
      field: "is_active",
      headerName: "Active",
      type: "boolean",
      renderCell: sexyBooleanColumn,
    },
    {
      field: "is_question_format",
      headerName: "Format",
      valueFormatter: (val) => (val ? "Question" : "Checkbox"),
    },
    {
      field: "event.title",
      headerName: "Event",
      valueGetter: dataGridNestedColumnGetter,
    },
    {
      field: "event_occurrence_offset",
      headerName: "Event Offset",
      valueGetter: dataGridNestedColumnGetter,
    },
    {
      field: "repeating",
      headerName: "Repeating",
      type: "boolean",
      renderCell: sexyBooleanColumnTrueOnly,
    },
    {
      field: "recurrence",
      headerName: "Repeat Rule",
      valueFormatter: formatRecurrenceRule,
    },
    {
      field: "next_occurrence_date",
      headerName: "Next Occurrence",
      valueGetter: (val) => (val ? dayjs(val).toDate() : null),
      type: "date",
    },
    {
      field: "repeat_start_date",
      headerName: "Repeat Start",
      valueGetter: (val) => (val ? dayjs(val).toDate() : null),
      type: "date",
    },
    {
      field: "repeat_end_date",
      headerName: "Repeat End",
      valueGetter: (val) => (val ? dayjs(val).toDate() : null),
      type: "date",
    },
    {
      field: "target_date",
      headerName: "Target Date",
      valueGetter: (val) => (val ? dayjs(val).toDate() : null),
      type: "date",
    },
    {
      field: "deadline",
      headerName: "Deadline",
      valueGetter: (val) => (val ? dayjs(val).toDate() : null),
      type: "date",
    },
    {
      field: "user_profiles",
      headerName: "Users",
      renderCell: renderOptionCell,
    },
    {
      field: "teams",
      headerName: "Teams",
      renderCell: renderOptionCell,
    },
    {
      field: "roles",
      headerName: "Roles",
      renderCell: renderOptionCell,
    },
    {
      headerName: "Actions",
      field: "actions",
      type: "actions",
      width: 75,
      getActions: ({ row }) => {
        return [
          <Tooltip title="Delete Template" key={3}>
            <GridActionsCellItem
              icon={<Delete color="primary" />}
              label="Delete Template"
              sx={{ p: 0, m: 0, fontSize: "small" }}
              onClick={() => {
                const targetAudience = [];
                if (row.user_profiles?.length > 0) {
                  targetAudience.push({
                    type: "Users",
                    items: row.user_profiles.map((u) => u.label || u.name),
                  });
                }
                if (row.teams?.length > 0) {
                  targetAudience.push({
                    type: "Teams",
                    items: row.teams.map((t) => t.label || t.name),
                  });
                }
                if (row.roles?.length > 0) {
                  targetAudience.push({
                    type: "Roles",
                    items: row.roles.map((r) => r.label || r.name),
                  });
                }

                confirm({
                  title: "Delete To-Do Item",
                  content: (
                    <Box>
                      <Typography variant="body1" sx={{ mb: 2 }}>
                        Are you sure you want to delete this to-do item?
                      </Typography>
                      <Typography variant="h6" color="error" sx={{ mb: 1 }}>
                        ⚠️ This to-do item is currently assigned to:
                      </Typography>
                      {targetAudience.length > 0 ? (
                        <Paper
                          sx={{
                            p: 2,
                            backgroundColor: "error.light",
                            color: "error.contrastText",
                          }}
                        >
                          {targetAudience.map((audience, index) => (
                            <Box key={index} sx={{ mb: 1 }}>
                              <Typography variant="subtitle2" fontWeight="bold">
                                {audience.type}:
                              </Typography>
                              <Typography variant="body2" sx={{ ml: 2 }}>
                                {audience.items.join(", ")}
                              </Typography>
                            </Box>
                          ))}
                        </Paper>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          No specific target audience assigned
                        </Typography>
                      )}
                      <Typography
                        variant="body2"
                        color="error"
                        sx={{ mt: 2, fontWeight: "bold" }}
                      >
                        This action cannot be undone and will affect all
                        assigned users, teams, and roles.
                      </Typography>
                    </Box>
                  ),
                })
                  .then(async () => {
                    const response = await api.delete(
                      apiUrls.admin.toDoTemplate(row.uuid)
                    );
                    if (response.data?.success) {
                      showSuccessSnackbar("Template deleted!");
                      setReload(true);
                    }
                  })
                  .catch(() => {});
              }}
            />
          </Tooltip>,
        ];
      },
    },
  ];

  return (
    <>
      <Drawer anchor="right" {...filterDisc.getDisclosureProps()}>
        <Box
          sx={{ paddingTop: `${layout.navBarHeight + 1}rem`, width: "25rem" }}
        >
          <Filters />
        </Box>
      </Drawer>
      <Box sx={{ height: "100%" }}>
        <PaginatedTable
          searchable
          tableName={"ToDoTemplateManagement"}
          addRowButtonText="Add Item"
          apiUrl={apiUrls.admin.toDoTemplates()}
          columns={columns}
          reload={reload}
          setReload={setReload}
          urlParams={{
            values: [
              "uuid",
              "event__title",
              "event_occurrence_offset",
              "checklist_items",
              "created_by",
              "deadline",
              "description",
              "is_active",
              "recurrence",
              "repeating",
              "repeat_start_date",
              "repeat_end_date",
              "next_occurrence_date",
              "is_question_format",
              "roles",
              "tags",
              "target_date",
              "teams",
              "title",
              "user_profiles",
            ],
            keep_related: true,
            related_as_options: true,
            search_params: {
              ...getFilterParams(),
              is_personal: false,
            },
          }}
          initialState={{
            pinnedColumns: {
              right: ["actions"],
            },
          }}
          sortingMode={sortingModes.SERVER}
          disableCellFocus
          getRowTitle={({ row }) => row?.title}
          FiltersAppliedList={FiltersAppliedList}
          onFiltersClick={filterDisc.onOpen}
          EditForm={ToDoTemplateForm}
          CreationForm={ToDoTemplateForm}
          CreationFormDialogProps={{
            maxWidth: "xs",
          }}
          EditFormDialogProps={{
            maxWidth: "xs",
          }}
        />
      </Box>
    </>
  );
}
